/*
 * Shopify Forms app blocks render inside an open shadow root (<shopify-forms-embed>),
 * so theme stylesheets cannot reach them. The app already sets direction: rtl, but it
 * hardcodes text-align: left on the field container and anchors the floating label with
 * `left: var(--form-field-padding)` — which leaves the placeholder labels on the wrong
 * side in Hebrew. This injects a mirrored stylesheet into each embed's shadow root.
 *
 * Class names are matched by substring because the app ships hashed suffixes
 * (e.g. _formInputField_nag3b_7) that change between app releases.
 */
(function () {
  if (document.documentElement.dir !== 'rtl') return;

  const CSS = `
    [class*="_formFieldContainer_"] {
      text-align: right;
    }

    [class*="_formInputField_"],
    [class*="_formFieldContainer_"] textarea {
      text-align: right;
    }

    label[class*="_formInputFieldLabel_"] {
      left: var(--form-field-padding);
      right: var(--form-field-padding);
      width: auto;
      text-align: right;
      transform-origin: right center;
    }

    [class*="_formFieldContainer_"]:focus-within label[class*="_formInputFieldLabel_"] {
      width: auto;
    }

    [class*="_formFieldContainer_"]:has(textarea)::before {
      left: 12px;
      right: 0;
    }
  `;

  const APPLIED = 'themeRtlApplied';
  const pending = new Set();
  let sheet = null;

  if (typeof CSSStyleSheet === 'function') {
    try {
      sheet = new CSSStyleSheet();
      sheet.replaceSync(CSS);
    } catch (error) {
      sheet = null;
    }
  }

  function apply(embed) {
    const root = embed.shadowRoot;
    if (!root) return false;
    if (root[APPLIED]) return true;
    root[APPLIED] = true;

    // Appended last so it also wins any same-specificity rule from the app's own sheets.
    if (sheet && Array.isArray(root.adoptedStyleSheets)) {
      root.adoptedStyleSheets = root.adoptedStyleSheets.concat(sheet);
    } else {
      const style = document.createElement('style');
      style.textContent = CSS;
      root.appendChild(style);
    }

    return true;
  }

  // attachShadow does not trigger MutationObserver, so embeds whose shadow root has not
  // been attached yet are polled for a short while after they appear in the DOM.
  let timer = null;

  function drain() {
    pending.forEach((embed) => {
      if (!embed.isConnected || apply(embed)) pending.delete(embed);
    });

    if (pending.size === 0 && timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function track(embed) {
    if (apply(embed)) return;
    pending.add(embed);
    if (!timer) timer = setInterval(drain, 100);
  }

  function scan(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.tagName === 'SHOPIFY-FORMS-EMBED') track(node);
    node.querySelectorAll('shopify-forms-embed').forEach(track);
  }

  scan(document.documentElement);

  // Popup and flyout forms mount long after page load, so keep watching.
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach(scan));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
